import type { BulkUserImportDraft } from '@/types/bulkUserImport';

export const bulkStaticDefaults = {
  flowTitle: 'Add a group of users',
  trustLine: 'AI proposes, you approve. No auto-actions on devices, users, or credentials.',
  progressRailSteps: [
    { id: 'read', label: 'Reading your file' },
    { id: 'match', label: 'Matching it to the template' },
    { id: 'add', label: 'Adding everyone to the list' },
    { id: 'check', label: "Checking each person's details" },
  ],
  noCsvProgressRailSteps: [{ id: 'prepare', label: 'Preparing for your upload' }],
  timing: {
    thinkingMs: 1400,
    progressStepMs: [1200, 1500, 1500, 1200] as const,
    almostReadyMs: 1000,
    formRevealMs: 800,
    savingMs: 2800,
  },
  explainability:
    'I read your CSV, matched columns to our user template, and prepared a review table before anything is saved.',
  noCsvAssistantHint:
    'Upload a CSV on the right to preview users before adding them. Review or edit anyone in the table, then approve when you are ready.',
  assistantHint:
    'All your people from the file you provided are in and matched the template, nothing missing. To change anything, click a field in the row to edit it, or just tell me what to change. Ready when you are.',
  credentialIssueTiming: {
    prepMs: 1000,
    issuingMs: 4500,
  },
  credentialIssueNextSteps: [
    { id: 'go-to-devices', label: 'Go to Devices' },
    { id: 'how-to-send-panels', label: 'How do I send these to my panels?' },
  ],
} as const;

export const bulkCsvPanelProcessingMs =
  bulkStaticDefaults.timing.thinkingMs +
  bulkStaticDefaults.timing.progressStepMs[0] +
  bulkStaticDefaults.timing.progressStepMs[1] +
  bulkStaticDefaults.timing.progressStepMs[2] +
  bulkStaticDefaults.timing.progressStepMs[3];

export function bulkUserSuccessIntro(site: string): string {
  return `Everyone from your file is now added to ${site}. Here is a quick summary of what happened and what to do next. You can keep track of everyone in the list on the right.`;
}

export function bulkUserSuccessBanner(count: number, site: string): string {
  return count === 1 ? `1 person added to ${site}.` : `${count} people added to ${site}.`;
}

export function bulkUserSuccessStatsLine(draft: BulkUserImportDraft): string {
  const count = draft.rows.length;
  const phoneSetup = draft.rows.filter((r) => r.hasMobile).length;
  const cardOrPin = draft.rows.filter(
    (r) => (r.hasPin ?? false) || Boolean(r.cardNumber?.trim())
  ).length;
  const peopleLabel = count === 1 ? '1 person' : `${count} people`;
  return `${peopleLabel} · ${phoneSetup} phone setup · ${cardOrPin} card or PIN`;
}

export function bulkUserSuccessNote(phoneSetup: number, cardOrPin: number): string {
  if (phoneSetup === 0 && cardOrPin === 0) {
    return 'People are saved in Unity. You can assign credentials and send them to your panels from Devices.';
  }
  if (phoneSetup > 0 && cardOrPin === 0) {
    const who = phoneSetup === 1 ? 'person will' : 'people will';
    return `${phoneSetup} ${who} set up on their phone. They will get a setup code by email, and you can issue their credential here once they are done.`;
  }
  if (phoneSetup === 0 && cardOrPin > 0) {
    const who = cardOrPin === 1 ? 'person is' : 'people are';
    return `The ${cardOrPin} ${who} set up with a card or PIN, saved in Unity. Cards and PINs open doors once you send them to your panels from Devices.`;
  }
  const phoneWho = phoneSetup === 1 ? 'person will' : 'people will';
  const cardWho = cardOrPin === 1 ? 'person is' : 'people are';
  return `${phoneSetup} ${phoneWho} set up on their phone. They will get a setup code by email, and you can issue their credential here once they are done. The other ${cardOrPin} ${cardWho} set up with a card or PIN, saved in Unity. Cards and PINs open doors once you send them to your panels from Devices.`;
}

export function bulkIssueAllMobileModalBody(count: number): string {
  const who = count === 1 ? 'person still needs' : 'people still need';
  return `${count} ${who} their mobile credential. Confirm they've each set up on their phone. Works at the door after your next Devices sync. Not everyone ready? Issue one at a time.`;
}

export function bulkIssueSingleMobileModalBody(firstName: string, lastName: string): string {
  const name = `${firstName} ${lastName}`.trim() || 'this person';
  return `Issue a mobile credential for ${name}? Confirm they've set up on their phone. Works at the door after your next Devices sync.`;
}

export function bulkIssueSingleMobileModalTitle(firstName: string, lastName: string): string {
  const name = `${firstName} ${lastName}`.trim() || 'this person';
  return `Issue mobile credential for ${name}?`;
}

export function bulkCredentialIssuingLabel(count: number): string {
  return count === 1 ? 'Issuing for 1 person' : `Issuing for ${count} people`;
}

export function bulkCredentialIssueSuccessIntro(count: number, site: string): string {
  const who = count === 1 ? 'mobile credential is' : 'mobile credentials are';
  return `All ${count} ${who} now issued for ${site}. Send them to your panels from Devices so they work at the door.`;
}
